(ns storrito.fabricjs.state
  (:require [reagent.core :as r]
            [goog.object :as obj]
            ["fabric" :refer [fabric]])
  (:refer-clojure :exclude [get assoc!]))

;; Concept:
;;
;; Helper methods to modify the current FabricJS canvas that is loaded
;; in the story editor.

(defonce state
  (r/atom {:canvas nil}))

(defn ^js/fabric.Canvas get-canvas
  "Returns the current FabricJS canvas that is loaded in the story
   editor."
  []
  (:canvas @state))

(defn mark-dirty!
  "Triggers a render of all Reagent components that invoked
   `get-canvas`."
  []
  (swap! state
         assoc ::refresh (rand)))

(defn set-canvas!
  "Should always be called, when a new FabricJS canvas is loaded in the
   story editor. Registers FabricJS event listeners to trigger a render
   of the story editor UI components whenever the canvas is changed."
  [canvas]
  (doseq [event ["selection:created"
                 "selection:updated"
                 "selection:cleared"
                 "text:editing:entered"
                 "text:editing:exited"
                 "text:selection:changed"
                 "text:changed"
                 "object:added"
                 "object:modified"
                 "object:removed"
                 "custom:object:dirty"]]
    (.on canvas
         event
         mark-dirty!))
  (swap! state
         assoc :canvas canvas)
  true)

(defn get-design
  "Gets the FabricJS object from the `fabric-canvas` that has a property
   'id' with the `object-id` as value."
  [^js/fabric.Canvas fabric-canvas object-id]
  (when object-id
    (let [objects (.getObjects fabric-canvas)]
      (some
        (fn [o]
          (when (= (.-id o) object-id)
            (.get(second (js->clj (.getObjects o))) "sticker-design")))
        objects))))

(defn get-by-id
  "Gets the FabricJS object from the `fabric-canvas` that has a property
   'id' with the `object-id` as value."
  [^js/fabric.Canvas fabric-canvas object-id]
  (when (and object-id
             fabric-canvas)
    (let [objects (.getObjects fabric-canvas)]
      (some
        (fn [o]
          (when (= (.-id o) object-id)
            o))
        objects))))

(defn by-id
  "Returns a map (object-id -> fabric-object) for the `fabric-canvas`."
  [^js/fabric.Canvas fabric-canvas]
  (into
    {}
    (keep
      (fn [o]
        (when-let [id (.-id o)]
          [id o]))
      (.getObjects fabric-canvas))))

(defn swap-object!
  "Gets the object with the given `object-id` from the `fabric-canvas`,
   applies the function `f` to it and replaces the object with the
   result of this function call."
  [^js/fabric.Canvas fabric-canvas object-id f]
  (let [objects (.getObjects fabric-canvas)]
    (some
      (fn [[idx o]]
        (when (= (.-id o) object-id)
          (let [o* (f o)]
            (.set o*
                  "id"
                  object-id)
            (.insertAt fabric-canvas
                       o*
                       idx
                       true)
            (.fire fabric-canvas
                   "custom:object:dirty"
                   #js {:target o*})
            o*)))
      (map vector
           (range)
           objects))))

(defn replace!
  "Replaces the object with the `object-id` in the `fabric-canvas` with
   the `fabric-object`."
  [^js/fabric.Canvas fabric-canvas object-id fabric-object]
  (swap-object! fabric-canvas
                object-id
                (fn [_]
                  fabric-object)))

(defn insert!
  "Inserts a new `fabric-object` with the given `object-id` (or the 'id'
   property of `fabric-object`) into the `fabric-canvas`. Does nothing
   if an object with the given `object-id` already exists in the
   `fabric-canvas`."
  ([^js/fabric.Canvas fabric-canvas object-id fabric-object]
   (when-not (get-by-id fabric-canvas
                        object-id)
     (.set fabric-object
             "id"
             object-id)
     (.add fabric-canvas
           fabric-object)))
  ([^js/fabric.Canvas fabric-canvas fabric-object]
   (assert (:id fabric-object))
   (insert! fabric-canvas
            (:id fabric-object)
            fabric-object)))

(defn get-active
  "Gets the active (selected) object on the `fabric-canvas`."
  ([^js/fabric.Canvas fabric-canvas]
   (when fabric-canvas
     (.getActiveObject fabric-canvas)))
  ([]
   (get-active (get-canvas))))

(defn set-active!
  "Sets the active (selected) object of the `fabric-canvas`."
  ([^js/fabric.Canvas fabric-canvas fabric-object]
   (when fabric-canvas
     (.setActiveObject fabric-canvas
                       fabric-object)
     (mark-dirty!)
     (.requestRenderAll fabric-canvas)
     fabric-canvas))
  ([fabric-object]
   (set-active! (get-canvas)
                fabric-object)))

(extend-type js/fabric.Object
  ;; allows to use clojure.core functions like get, get-in,
  ;; select-keys to be used on FabricJS objects.
  ILookup
  (-lookup
    ([coll k]
     (-lookup coll k nil))
    ([coll k not-found]
     (obj/get coll
              (name k)
              not-found))))

(defn mark-object-dirty!
  "Marks the `fabric-object` as 'dirty'. Thereby it will be repainted on
   the next render cycle. Sends a 'custom:object:dirty' to the FabricJS
   canvas object to trigger a call of `mark-dirty!`."
  [^js/fabric.Object fabric-object]
  (.setCoords fabric-object)
  (.set fabric-object
        "dirty"
        true)
  (when-let [fabric-canvas (or (.-canvas fabric-object)
                               ;; TODO: normally the canvas property
                               ;;       should always be set to the
                               ;;       Fabric canvas to which this
                               ;;       `fabric-object` has been
                               ;;       added. However this seems not
                               ;;       always be the case, see:
                               ;;       https://sentry.io/organizations/vire-gmbh/issues/910134019
                               (get-canvas))]
    (.fire fabric-canvas
           "custom:object:dirty"
           #js {:target fabric-object})

    ;; trigger repaint:
    (.renderAll fabric-canvas)))

(defn assign!
  "Assigns given `properties` to the `fabric-object` JS
   object. `properties` is a Clojure map and keyword keys are
   supported."
  [^js/fabric.Object fabric-object properties]
  (when fabric-object
    (doseq [[k v] properties]
      (.set fabric-object
            (name k)
            v))
    (mark-object-dirty! fabric-object)
    fabric-object))

(defn update!
  "Replaces the value of `key` from `fabric-object` with `(f
   value)`. The `key` can be a Keyword."
  [^js/fabric.Object fabric-object key f]
  (let [key (name key)]
    (assign! fabric-object
             {key (f (obj/get fabric-object
                              key))})
    fabric-object))

(defn assoc!
  "Sets the all key value pairs `kvs` on the `fabric-object`."
  [^js/fabric.Object fabric-object & kvs]
  (assign! fabric-object
           (partition 2 kvs))
  fabric-object)

(defn is-text-editing?
  "Returns true, if the active object is an IText in editing mode, in a fabricjs edition"
  []
  (boolean
    (when-let [fabric-object (get-active)]
      (and (= (:type fabric-object)
              "i-text")
           (:isEditing fabric-object)))))

(defn is-text-selected?
  "Returns true, if the `fabric-object` is an IText, where a text part
   has been selected."
  [^js/fabric.Object fabric-object]
  (and (= (:type fabric-object)
          "i-text")
       (not (empty? (.getSelectedText fabric-object)))))

(defn assign-selection-styles!
  "Assigns given `properties` to text selection of the `fabric-object`
   JS object. `properties` is a Clojure map and keyword keys are
   supported."
  [^js/fabric.Object fabric-object properties]
  (when (is-text-selected? fabric-object)
    (.setSelectionStyles fabric-object
                         (clj->js properties))
    (mark-object-dirty! fabric-object)
    fabric-object))

(defn set-element!
  "Sets the `element` on the `fabric-object` while preserving its size."
  [fabric-object element]
  (let [width (.-width fabric-object)
        height (.-height fabric-object)]
    (.setElement fabric-object
                 element)
    (set! (.-width fabric-object)
          width)
    (set! (.-height fabric-object)
          height)))

(defn get-object-ids
  "Returns the ids of all objects in the `fabric-canvas`."
  [fabric-canvas]
  (map (fn [fabric-object]
         (obj/get fabric-object
                  "id"))
       (.getObjects fabric-canvas)))

(def +mention-limit+
  20)

(defn get-mentions
  "returns the "
  [^js/fabric.Canvas fabric-canvas]
  (when fabric-canvas
    (let [objects (.getObjects fabric-canvas)]
      (filter (fn [object]
                (or (and (.-text object)
                      (.startsWith (.-text object) "@"))
                  (.startsWith (.-id object) "user")))
        objects))))

(def +hashtag-limit+
  5)

(defn get-hashtags
  [^js/fabric.Canvas fabric-canvas]
  (when fabric-canvas
    (let [objects (.getObjects fabric-canvas)]
      (filter (fn [object]
                (or (and (.-text object)
                      (.startsWith (.-text object) "#"))
                  (.startsWith (.-id object) "hash")))
        objects))))
